import { db } from '@app/icare/client/db'
import type { ClientDatabase } from '@app/icare/client/db/schema'
import type { ClientEntityByName, ClientTableName } from '@app/icare/client/db/types'
import type { EntityId } from '@pkg/db/schema'
import { getTableById } from '@pkg/db/utils'
import { type DefaultError, queryOptions, type UseQueryOptions } from '@tanstack/react-query'
import type { SelectQueryBuilder, SimplifySingleResult } from 'kysely'
import type { LocalQueryMeta, LocalQueryTables } from './localQueryOptions'

type NullableSimplifySingleResult<S> = SimplifySingleResult<S> | null

interface LocalQueryOneOptions<
  TB extends keyof ClientDatabase,
  S,
  TQueryFnData extends NullableSimplifySingleResult<S> = NullableSimplifySingleResult<S>,
  TError = DefaultError,
  TData = TQueryFnData,
> extends Omit<UseQueryOptions<TQueryFnData, TError, TData>, 'queryKey' | 'queryFn' | 'initialData'> {
  query: SelectQueryBuilder<ClientDatabase, TB, S>
  tables?: LocalQueryTables
}

export function localQueryOneOptions<
  TB extends keyof ClientDatabase,
  S,
  TQueryFnData extends NullableSimplifySingleResult<S> = NullableSimplifySingleResult<S>,
  TError = DefaultError,
  TData = TQueryFnData,
>(opts: LocalQueryOneOptions<TB, S, TQueryFnData | null, TError, TData>) {
  const { query, tables, meta, ...rest } = opts
  const { sql, parameters } = query.compile()

  const keyParams = JSON.stringify(parameters)

  return queryOptions<TQueryFnData, TError, TData>({
    queryKey: ['app', 'query', 'one', sql, keyParams],
    meta: { ...(meta ?? {}), tables, sql, parameters } satisfies LocalQueryMeta,
    ...rest,
    queryFn: () => query.executeTakeFirst().then(r => (r ?? null) as never),
  } as never)
}

export function localQueryEntityOptions<E extends ClientTableName>(
  id?: EntityId<E> | 'new' | null | undefined | never,
  opts?: Omit<
    LocalQueryOneOptions<
      E,
      ClientEntityByName<E>,
      SimplifySingleResult<ClientEntityByName<E>>,
      DefaultError,
      SimplifySingleResult<ClientEntityByName<E>>
    >,
    'query'
  >,
) {
  const table = id && id !== 'new' ? getTableById(id) : undefined
  return localQueryOneOptions<
    E,
    ClientEntityByName<E>,
    SimplifySingleResult<ClientEntityByName<E>>,
    DefaultError,
    SimplifySingleResult<ClientEntityByName<E>>
  >({
    ...opts,
    tables: table ? ([table] as LocalQueryTables) : opts?.tables,
    enabled: id !== 'new' && !!id && !!table && opts?.enabled,
    // Uses sqlite_master.type instead of <table>.id if no table could be determined
    // to still have a valid query in case it is executed despite `enabled` above being set to false
    query: db
      .selectFrom((table as never) ?? 'sqlite_master')
      .selectAll()
      .where(table ? ('id' as never) : ('type' as never), '=', id as never),
  } as never)
}
